import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react"
import type { ReactNode } from "react"
import type { FieldCheck } from "./EntityCompletenessSidebar"

export interface CompletenessState {
  entityName: string
  entityId: string
  data: unknown
  fieldChecks: FieldCheck<unknown>[]
  title?: string
}

interface EntityCompletenessContextValue {
  state: CompletenessState | null
  register: (key: symbol, value: CompletenessState | null) => void
  unregister: (key: symbol) => void
}

const EntityCompletenessContext = createContext<EntityCompletenessContextValue>({
  state: null,
  register: () => {},
  unregister: () => {},
})

export function EntityCompletenessProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CompletenessState | null>(null)
  const registry = useRef<Map<symbol, CompletenessState | null>>(new Map())

  const recompute = useCallback(() => {
    const entries = Array.from(registry.current.values()).filter((v): v is CompletenessState => v !== null)
    if (entries.length === 0) {
      setState(null)
      return
    }
    if (entries.length > 1 && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[EntityCompletenessContext] ${entries.length} setters actifs simultanément — le dernier monté gagne`
      )
    }
    setState(entries[entries.length - 1])
  }, [])

  const register = useCallback(
    (key: symbol, value: CompletenessState | null) => {
      registry.current.set(key, value)
      recompute()
    },
    [recompute]
  )

  const unregister = useCallback(
    (key: symbol) => {
      registry.current.delete(key)
      recompute()
    },
    [recompute]
  )

  return (
    <EntityCompletenessContext.Provider value={{ state, register, unregister }}>
      {children}
    </EntityCompletenessContext.Provider>
  )
}

export function useEntityCompleteness(): CompletenessState | null {
  return useContext(EntityCompletenessContext).state
}

export function useSetEntityCompleteness<T>(value: {
  entityName: string
  entityId: string
  data: T | undefined
  fieldChecks: FieldCheck<T>[]
  title?: string
} | null) {
  const { register, unregister } = useContext(EntityCompletenessContext)
  const keyRef = useRef<symbol>(Symbol("entity-completeness"))

  useEffect(() => {
    const key = keyRef.current
    if (value === null) {
      unregister(key)
      return
    }
    register(key, value as unknown as CompletenessState)
    return () => unregister(key)
  }, [value, register, unregister])
}
